package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PrimaryScrollableTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.feature.operacional.MonthPeriodSelector
import br.com.vvrefeicoes.portal.navigation.canAccess
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Users
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_panel_all_directors
import portalvv.shared.generated.resources.rh_panel_tab_dismissed
import portalvv.shared.generated.resources.rh_panel_tab_overtime
import portalvv.shared.generated.resources.rh_panel_tab_summary
import portalvv.shared.generated.resources.rh_panel_tab_vacancies
import portalvv.shared.generated.resources.rh_panel_title

private val DIRECTOR_FILTER_MAX_WIDTH = 320.dp

private enum class RhPanelTab(
    val title: StringResource,
) {
    SUMMARY(Res.string.rh_panel_tab_summary),
    OVERTIME(Res.string.rh_panel_tab_overtime),
    DISMISSED(Res.string.rh_panel_tab_dismissed),

    /** As vagas vêm da RMP: a aba só aparece para quem tem a permissão do painel da RMP. */
    VACANCIES(Res.string.rh_panel_tab_vacancies),
}

@Composable
fun RhPanelScreen() {
    val controller = rememberRhPanelController()
    val permissions = rememberSessionPermissions()
    val tabs =
        remember(permissions) {
            RhPanelTab.entries.filter { tab -> tab != RhPanelTab.VACANCIES || canAccess(permissions.keys, PermissionKeys.RMP) }
        }
    var selectedTab by remember { mutableStateOf(RhPanelTab.SUMMARY) }
    var selectedMonth by remember { mutableStateOf(SystemDateTimeProvider().currentPeriod()) }
    val currentTab = if (selectedTab in tabs) selectedTab else RhPanelTab.SUMMARY

    LaunchedEffect(selectedMonth) { controller.applyPeriod(selectedMonth) }
    LaunchedEffect(currentTab) {
        if (currentTab == RhPanelTab.VACANCIES) controller.loadVacancies()
    }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        footer = { AppFooter() },
    ) {
        Text(
            text = stringResource(Res.string.rh_panel_title),
            style = MaterialTheme.typography.headlineMedium,
        )

        RhPanelFilters(
            controller = controller,
            showPeriodFilters = currentTab != RhPanelTab.VACANCIES,
            selectedMonth = selectedMonth,
            onMonthChange = { selectedMonth = it },
        )

        PrimaryScrollableTabRow(selectedTabIndex = tabs.indexOf(currentTab)) {
            tabs.forEach { tab ->
                Tab(
                    selected = tab == currentTab,
                    onClick = { selectedTab = tab },
                    text = { Text(text = stringResource(tab.title)) },
                )
            }
        }

        if (currentTab == RhPanelTab.VACANCIES) {
            val vacancies = controller.vacancies
            val vacanciesError = controller.vacanciesError
            when {
                vacancies != null -> RhVacanciesContent(vacancies)
                vacanciesError != null -> RhLoadError(message = vacanciesError, onRetry = { controller.loadVacancies(force = true) })
                else -> RhLoading()
            }
        } else {
            val summary = controller.summary
            val dismissed = controller.dismissed
            val overtime = controller.overtime
            val errorMessage = controller.errorMessage
            when {
                controller.loading -> RhLoading()
                errorMessage != null -> RhLoadError(message = errorMessage, onRetry = controller::retry)
                currentTab == RhPanelTab.SUMMARY && summary != null -> RhSummaryContent(summary)
                currentTab == RhPanelTab.OVERTIME && overtime != null -> RhOvertimeContent(overtime)
                currentTab == RhPanelTab.DISMISSED && dismissed != null -> RhDismissedContent(dismissed)
            }
        }
    }
}

@Composable
private fun RhPanelFilters(
    controller: RhPanelController,
    showPeriodFilters: Boolean,
    selectedMonth: MonthPeriod,
    onMonthChange: (MonthPeriod) -> Unit,
) {
    if (!showPeriodFilters) return
    val allDirectors = stringResource(Res.string.rh_panel_all_directors)
    val selectedDirector = controller.director
    // A diretoria selecionada continua na lista mesmo que o mês escolhido não tenha dados dela.
    val directors = (controller.summary?.directors.orEmpty() + listOfNotNull(selectedDirector)).distinct()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SearchableSelect(
                leadingIcon = Lucide.Users,
                options = listOf(allDirectors) + directors,
                selected = selectedDirector ?: allDirectors,
                onSelect = { option -> controller.selectDirector(option.takeIf { it != allDirectors }) },
                modifier = Modifier.weight(1f, fill = false).widthIn(max = DIRECTOR_FILTER_MAX_WIDTH),
            )
            MonthPeriodSelector(
                month = selectedMonth,
                onMonthChange = onMonthChange,
            )
        }
    }
}
